/**
 * B080040 店別ピッキングリスト発行
 */
import { Column, Query } from "@material-table/core";
import {
  B000223BatchNm,
  B080040Find,
  B080040FindRequest,
  B080040Print,
  B080040PrintRequest,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SConfirmDialog,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { STheme } from "components/styles";
import { getClientError, getClientMessage } from "messages";
import { getSCode, HyoziShukajokyo, HyoziPickingjokyo } from "messages/codes";
import { labels } from "messages/labels";
import { B000223 } from "pages";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, dateWithSlashAt, download, mapIds, printed } from "services";
import { sutiShukajokyo, pickinghako, cancelKbn } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
  tenposu: string;
  shohinsu: string;
  shukajokyo: number;
  pickinghako: number;
  shukajokyoView: number;
  pickinghakoView: number;
  cancelKbn: number;
  cancelKbnView: string;
  version: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shukashijiYmd: date(),
  shijikaisu: "",
  batchNo: "",
  batchNm: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  {
    field: "shukashijiYmd",
    title: "出荷指示日",
    width: "5rem",
    render: (data) => {
      if (data.shukashijiYmd! !== null && data.shukashijiYmd! !== "") {
        return dateWithSlashAt(data.shukashijiYmd!);
      }
    },
  },
  { field: "shijikaisu", title: "指示回数　", width: "3rem", align: "right" },
  { field: "batchNo", title: "バッチ番号　", width: "3rem", align: "right" },
  { field: "batchNm", title: "バッチ名", width: "12rem" },
  { field: "tenposu", title: "店舗数　", width: "3rem", align: "right" },
  { field: "shohinsu", title: "商品数　", width: "4rem", align: "right" },
  {
    field: "shukajokyoView",
    title: "出荷状況",
    width: "6rem",
    align: "center",
    render: (data) => {
      const text = getSCode(
        HyoziShukajokyo,
        data.shukajokyo?.toString() ?? "0"
      )?.label;
      // 未処理
      let color = STheme.text.red;
      if (data.shukajokyo === sutiShukajokyo.shijizumi) {
        // 指示済み
        color = STheme.text.blue;
      } else if (data.shukajokyo === sutiShukajokyo.kakuteizumi) {
        // 確定済み
        color = STheme.text.green;
      }
      return <SText color={color}>{text}</SText>;
    },
  },
  { field: "shukajokyo", hidden: true },
  {
    field: "cancelKbnView",
    title: "キャンセル有",
    width: "4rem",
    align: "center",
    render: (data) => {
      if (data.cancelKbn === cancelKbn.sumi) {
        return "◯";
      }
      if (data.cancelKbn === cancelKbn.zen) {
        return "全";
      }
      return "";
    },
  },
  { field: "cancelKbn", hidden: true },
  {
    field: "pickinghakoView",
    title: "店別発行状況",
    width: "6rem",
    render: (data) => {
      const text = getSCode(
        HyoziPickingjokyo,
        data.pickinghako?.toString() ?? "0"
      )?.label;
      // 未処理
      let color = STheme.text.red;
      if (data.pickinghako === pickinghako.tenbetuhakozumi) {
        // 指示済み
        color = STheme.text.blue;
      }
      return <SText color={color}>{text}</SText>;
    },
  },
  { field: "pickinghako", hidden: true },
  { field: "cancelKbn", hidden: true },
  { field: "version", hidden: true },
];

const focusId = "B080040";

export const B080040 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  const pageSize = 300;

  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);

  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);

  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  });

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移の処理
  const navigate = useNavigate();

  // selectboxで選択された行のデータ
  const [selectedData, setSelectedData] = useState<Data[]>([]);

  // ボタンの活性・非活性
  const [disabledPrint, setDisabledPrint] = useState(true);

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // ボタン非活性化
    setDisabledPrint(true);
    // 選択されているデータを空にする
    setSelectedData([]);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButtun = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B080040FindRequest = {
        shukashijiYmd: fields.shukashijiYmd!,
        shijikaisu: fields.shijikaisu,
        batchNo: fields.batchNo,
        page: query.page,
        sort: ["shukashijiYmd,asc", "shijikaisu,asc", "batchNo,asc"],
        size: query.pageSize,
      };

      // API呼び出し
      B080040Find(param)
        .then((value) => {
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B080040_001"));
          }
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行チェックボックス選択時
  const handleCheckTableRow = (data: Data[]) => {
    // 行が選択されている場合のみ実行する。
    const disabled = data.length <= 0;
    setDisabledPrint(disabled);
    setSelectedData(data);
  };

  // 印刷イベント
  const handlePrint = () => {
    // リクエスト作成
    const param: B080040PrintRequest = {
      b080040PrintRequestChildList: selectedData.map((value) => {
        return {
          shukashijiYmd: value.shukashijiYmd,
          shijikaisu: value.shijikaisu,
          batchNo: value.batchNo,
          shukajokyo: value.shukajokyo,
          cancelKbn: value.cancelKbn,
          version: value.version,
        };
      }),
    };

    // 実行API呼び出し
    B080040Print(param)
      .then((response) => {
        //テーブル更新の為再検索
        (tableRef.current as any).onQueryChange();
        // ボタン非活性化
        setDisabledPrint(true);
        // 選択されているデータを空にする
        setSelectedData([]);
        if (printed(response)) {
          setMsg(getClientMessage("B080040_001"));
        } else {
          download(response);
          setMsg(getClientMessage("B080040_002"));
        }
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="店別ピッキングリスト発行"
        gridTemplateColumns="6rem 9rem 6rem 11rem 12rem 2rem"
      >
        <SLabel required>出荷指示日</SLabel>
        <SDateField
          name="shukashijiYmd"
          {...register(1)}
          required
          label="出荷指示日"
        />
        <SLabel>指示回数</SLabel>
        <SNumberField name="shijikaisu" size={4} {...register(2)} />
        <SLabel gridColumn={3}>バッチ番号</SLabel>
        <SNumberField
          name="batchNo"
          code
          size={4}
          onBlur={() => B000223BatchNm(methods, navigate, setMsg)}
          {...register(3)}
          dialog={<B000223 />}
          handleSubmit={(data: any) => {
            methods.setValue("batchNo", data.batchNo);
            methods.setValue("batchNm", data.batchNm);
          }}
        />
        <STextField name="batchNm" size={40} disabled />
        <SNormalButton
          label={labels.buttons.search}
          gridRow={4}
          gridColumn={5}
          onClick={handleClickSearchButtun}
          align={"right"}
          {...register(4)}
        />
      </SFrame>
      <SFrame gridTemplateColumns="74.7rem">
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onSelectionChange={handleCheckTableRow}
          handleSetDisabled={(rowData) => rowData.cancelKbn === cancelKbn.zen}
          width={1260}
          height={440}
          pageSize={pageSize}
          multi
          onPageChange={handleResetSearch}
        />
        <SFileButton
          label={labels.buttons.print}
          gridRow={2}
          gridColumn={2}
          {...register(5)}
          disabled={disabledPrint}
          dialog={
            <SConfirmDialog title="印刷" msg="印刷します。よろしいですか？" />
          }
          dialogAction="confirm"
          handleSubmit={handlePrint}
          align={"right"}
        />
      </SFrame>
    </FormProvider>
  );
};
